import { useState, useRef } from "react";
import { useLocation } from "wouter";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useToast } from "@/hooks/use-toast";
import { useIsMobile } from "@/hooks/use-mobile";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Shield, Tag } from "lucide-react";
import { formatCurrency, type PricingBreakdown } from "@/lib/pricing-utils";
import { BOOK_DIRECT_SHORT, STRIPE_SECURE_SHORT } from "@shared/property-facts";
import { FormWizard, type WizardStep } from "@/components/ui/form-wizard";

const guestInfoSchema = z.object({
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  email: z.string().trim().email("Please enter a valid email address"),
  phone: z.string().min(1, "Phone number is required"),
  specialRequests: z.string().optional(),
});

type GuestInfoForm = z.infer<typeof guestInfoSchema>;

interface BookingData {
  checkIn: string;
  checkOut: string;
  guests: number;
  nights: number;
  total: string;
  breakdown: PricingBreakdown['breakdown'];
  securityDeposit?: string | null;
  couponCode?: string | null;
  paymentPlan?: 'full' | 'split';
  propertyId?: string;
  propertySlug?: string;
}

interface GuestInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
  bookingData: BookingData | null;
}

export function GuestInfoModal({ isOpen, onClose, bookingData }: GuestInfoModalProps) {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const leadSavedRef = useRef(false);

  const trySaveLead = async () => {
    const email = form.getValues("email");
    if (!email || leadSavedRef.current || !bookingData) return;
    if (!z.string().email().safeParse(email).success) return;
    leadSavedRef.current = true;
    try {
      await fetch("/api/checkout-leads", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          firstName: form.getValues("firstName"),
          lastName: form.getValues("lastName"),
          checkIn: bookingData.checkIn,
          checkOut: bookingData.checkOut,
          guests: bookingData.guests,
        }),
      });
    } catch {
      leadSavedRef.current = false;
    }
  };

  const handleClose = () => {
    void trySaveLead();
    onClose();
  };

  const form = useForm<GuestInfoForm>({
    resolver: zodResolver(guestInfoSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      specialRequests: "",
    },
  });

  const onSubmit = async (data: GuestInfoForm) => {
    if (!bookingData) return;

    setIsSubmitting(true);

    try {
      // Capture the lead even if they abandon on the payment step.
      if (!leadSavedRef.current) {
        leadSavedRef.current = true;
        try {
          await fetch("/api/checkout-leads", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              email: data.email,
              firstName: data.firstName,
              lastName: data.lastName,
              checkIn: bookingData.checkIn,
              checkOut: bookingData.checkOut,
              guests: bookingData.guests,
              property: bookingData.propertySlug,
              property_id: bookingData.propertyId,
            }),
          });
        } catch {
          leadSavedRef.current = false;
        }
      }

      const nightlyRateItem = bookingData.breakdown?.find(item => item.label.toLowerCase().includes('night'));
      sessionStorage.setItem("pendingBooking", JSON.stringify({
        bookingId: `booking-${Date.now()}`,
        checkIn: bookingData.checkIn,
        checkOut: bookingData.checkOut,
        guests: bookingData.guests,
        total: bookingData.total,
        currency: "usd",
        nights: bookingData.nights,
        nightlyRate: nightlyRateItem?.amount || "0",
        breakdown: bookingData.breakdown,
        securityDeposit: bookingData.securityDeposit || null,
        couponCode: bookingData.couponCode || null,
        paymentPlan: bookingData.paymentPlan || 'full',
        propertyId: bookingData.propertyId,
        propertySlug: bookingData.propertySlug,
      }));

      sessionStorage.setItem("guestInfo", JSON.stringify(data));

      onClose();
      setLocation("/booking/checkout");
    } catch (error: any) {
      toast({
        title: "Booking Error",
        description: error.message || "Failed to save booking details. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const steps: WizardStep[] = [
    {
      title: "Your name",
      validate: () => form.trigger(["firstName", "lastName"]),
      content: (
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="firstName" className="text-luxury-xs">First name *</Label>
            <Input id="firstName" placeholder="John" autoComplete="given-name" {...form.register("firstName")} className="h-11" data-testid="modal-first-name" />
            {form.formState.errors.firstName && (
              <p className="text-luxury-xs text-destructive">{form.formState.errors.firstName.message}</p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="lastName" className="text-luxury-xs">Last name *</Label>
            <Input id="lastName" placeholder="Smith" autoComplete="family-name" {...form.register("lastName")} className="h-11" data-testid="modal-last-name" />
            {form.formState.errors.lastName && (
              <p className="text-luxury-xs text-destructive">{form.formState.errors.lastName.message}</p>
            )}
          </div>
        </div>
      ),
    },
    {
      title: "Contact",
      validate: () => form.trigger(["email", "phone"]),
      content: (
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="email" className="text-luxury-xs">Email *</Label>
            <Input
              id="email"
              type="email"
              inputMode="email"
              autoComplete="email"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              placeholder="john@example.com"
              {...form.register("email")}
              onBlur={() => void trySaveLead()}
              className="h-11"
              data-testid="modal-email"
            />
            {form.formState.errors.email && (
              <p className="text-luxury-xs text-destructive">{form.formState.errors.email.message}</p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="phone" className="text-luxury-xs">Phone *</Label>
            <Input id="phone" type="tel" autoComplete="tel" placeholder="+1 (555) 123-4567" {...form.register("phone")} className="h-11" data-testid="modal-phone" />
            {form.formState.errors.phone && (
              <p className="text-luxury-xs text-destructive">{form.formState.errors.phone.message}</p>
            )}
          </div>
        </div>
      ),
    },
    {
      title: "Final details",
      content: (
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="specialRequests" className="text-luxury-xs">Special requests (optional)</Label>
            <Textarea
              id="specialRequests"
              placeholder="Early check-in, late checkout, etc."
              {...form.register("specialRequests")}
              rows={2}
              className="resize-none"
              data-testid="modal-special-requests"
            />
          </div>
          {bookingData && (
            <div className="bg-sand border border-sand-deep p-4 space-y-2">
              <div className="flex justify-between items-center mb-1">
                <p className="text-luxury-xs font-serif text-ink-soft">Price details</p>
                <div className="flex items-center gap-1 text-luxury-xs text-ink-soft">
                  <Shield className="h-3 w-3" />
                  <span>{STRIPE_SECURE_SHORT}</span>
                </div>
              </div>
              <p className="flex items-center gap-1 text-luxury-xs text-ink-soft mb-2">
                <Tag className="h-3 w-3" />
                <span>{BOOK_DIRECT_SHORT}</span>
              </p>
              {bookingData.breakdown.map((item, i) => (
                <div key={i} className="flex justify-between text-luxury-xs font-serif font-normal">
                  <span className="text-ink-soft">{item.label}</span>
                  <span className={item.amount.startsWith('-') ? 'text-palm' : ''}>
                    {item.amount.startsWith('-') ? item.amount : formatCurrency(item.amount)}
                  </span>
                </div>
              ))}
              <div className="flex justify-between font-serif font-normal text-luxury-sm pt-2 border-t border-sand-deep">
                <span>Total</span>
                <span>{formatCurrency(parseFloat(bookingData.total))}</span>
              </div>
              {bookingData.securityDeposit && parseFloat(bookingData.securityDeposit) > 0 && (
                <div className="flex justify-between text-luxury-xs font-serif font-normal pt-2 border-t border-sand-deep">
                  <div>
                    <span className="text-ink-soft">Refundable security deposit</span>
                    <p className="text-luxury-xs text-ink-soft/70">Charged separately. Returned after checkout.</p>
                  </div>
                  <span className="text-ink-soft">{formatCurrency(bookingData.securityDeposit)}</span>
                </div>
              )}
            </div>
          )}
          <p className="text-luxury-xs text-center text-ink-soft">
            You won't be charged until you complete payment
          </p>
        </div>
      ),
    },
  ];

  const formContent = (
    <FormWizard
      steps={steps}
      onComplete={form.handleSubmit(onSubmit)}
      submitLabel="Continue to Payment"
      submittingLabel="Processing..."
      isSubmitting={isSubmitting}
    />
  );

  if (isMobile) {
    return (
      <Sheet open={isOpen} onOpenChange={(open) => !open && handleClose()}>
        <SheetContent side="bottom" className="h-[90vh] overflow-y-auto rounded-t-2xl">
          <SheetHeader className="mb-4">
            <SheetTitle className="text-luxury-xl font-serif font-normal tracking-luxury-tight">
              Complete Your Booking
            </SheetTitle>
            <SheetDescription className="text-luxury-sm">
              Enter your details to proceed to secure payment
            </SheetDescription>
          </SheetHeader>
          {formContent}
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-luxury-xl font-serif font-normal tracking-luxury-tight">
            Complete Your Booking
          </DialogTitle>
          <DialogDescription className="text-luxury-sm">
            Enter your details to proceed to secure payment
          </DialogDescription>
        </DialogHeader>
        {formContent}
      </DialogContent>
    </Dialog>
  );
}
